import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createCashOut, getCashOut, updateCashOut, getChangerStatus } from '../services/cashOutService';
import { useLocationContext } from '../contexts/LocationContext';
import { track } from '../services/analyticsService';

// Resolve a math expression like "6500 - 5830" or "120 + 95 + 200" to its
// computed value. Returns the original string if it isn't a clean expression.
const evaluateExpression = (str) => {
  if (str == null) return '';
  const cleaned = String(str).trim();
  if (!cleaned) return '';
  // Allow only digits, the four basic operators, parens, decimals, whitespace
  if (!/^[\d+\-*/().\s]+$/.test(cleaned)) return cleaned;
  // Plain number — no eval needed
  if (/^-?\d+(\.\d+)?$/.test(cleaned)) return cleaned;
  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${cleaned})`)();
    if (typeof result === 'number' && Number.isFinite(result)) {
      return String(Math.round(result * 100) / 100);
    }
  } catch {
    // fall through
  }
  return cleaned;
};

// Numeric input that evaluates math expressions on blur. Forwards all props
// to the underlying <input>; calls onChange with a synthetic event carrying
// the resolved value if the expression simplified.
const MathInput = ({ onChange, onBlur, ...rest }) => (
  <input
    {...rest}
    onChange={onChange}
    onBlur={(e) => {
      const result = evaluateExpression(e.target.value);
      if (result !== e.target.value && onChange) {
        onChange({ target: { value: result } });
      }
      if (onBlur) onBlur(e);
    }}
  />
);

const PoolToggle = ({ pooled, onToggle }) => (
  <div className="inline-flex rounded-md shadow-sm ml-3" role="group">
    <button
      type="button"
      onClick={() => onToggle(false)}
      className={`px-3 py-1 text-xs font-medium rounded-l-md border ${
        !pooled
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
    >
      Individual
    </button>
    <button
      type="button"
      onClick={() => onToggle(true)}
      className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-b border-r ${
        pooled
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
    >
      Pooled
    </button>
  </div>
);

const CashOutForm = () => {
  const { currentLocation } = useLocationContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [coinsInUnit, setCoinsInUnit] = useState('buckets'); // 'buckets' or 'dollars'
  const [changerMode, setChangerMode] = useState('detailed'); // 'detailed' or 'simple'

  // Section inclusion state (which sections to count in this cash out)
  const [ssIncluded, setSsIncluded] = useState(true);
  const [autoIncluded, setAutoIncluded] = useState(true);
  const [vacIncluded, setVacIncluded] = useState(true);
  const [vendingIncluded, setVendingIncluded] = useState(true);
  const [dogIncluded, setDogIncluded] = useState(true);

  // Pooled state
  const [ssPooled, setSsPooled] = useState(currentLocation?.default_ss_pooled || false);
  const [vacPooled, setVacPooled] = useState(currentLocation?.default_vac_pooled || false);
  const [vendingPooled, setVendingPooled] = useState(currentLocation?.default_vending_pooled || false);
  const [dogPooled, setDogPooled] = useState(currentLocation?.default_dog_pooled || false);
  const [autoPooled, setAutoPooled] = useState(currentLocation?.default_auto_pooled || false);
  const [changerPooled, setChangerPooled] = useState(currentLocation?.default_changer_pooled || false);

  // Derive counts from location settings
  const numChangers = currentLocation?.num_changers ?? 2;
  const numAutoWashes = currentLocation?.num_auto_washes ?? 1;
  const numSSBays = currentLocation?.num_ss_bays ?? 4;
  const numVacuums = currentLocation?.num_vacuums ?? 7;
  const vendingNames = currentLocation?.vending_machine_names ?? ['Front Vend', 'Back Vend', 'Fragrance'];
  // 0 by default: only washes that configure a dog wash ever see the section.
  const numDogWashes = currentLocation?.num_dog_washes ?? 0;

  const makeChangers = (count) =>
    Array.from({ length: count }, (_, i) => ({
      changer_number: i + 1, coins_in_dollars: '', coins_out_quarters: '', bills_out_dollars: ''
    }));
  const makeAutoWashes = (count) =>
    Array.from({ length: count }, (_, i) => ({
      wash_number: i + 1, coins_in_dollars: '', coins_out_quarters: '', bills_out_dollars: ''
    }));
  const makeSSBays = (count) =>
    Array.from({ length: count }, (_, i) => ({
      bay_number: i + 1, bills: '', coins_quarters: ''
    }));
  const makeVacuums = (count) =>
    Array.from({ length: count }, (_, i) => ({
      vacuum_number: i + 1, coins_quarters: ''
    }));
  const makeVending = (names) =>
    names.map(name => ({ machine_name: name, bills: '', coins_quarters: '' }));
  const makeDogWashes = (count) =>
    Array.from({ length: count }, (_, i) => ({
      dog_wash_number: i + 1, bills: '', coins_quarters: ''
    }));

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    changers: makeChangers(numChangers),
    auto_washes: makeAutoWashes(numAutoWashes),
    ss_bays: makeSSBays(numSSBays),
    vacuums: makeVacuums(numVacuums),
    vending_machines: makeVending(vendingNames),
    dog_washes: makeDogWashes(numDogWashes),
    quarter_buckets_deposited: '',
  });

  // Pooled single-entry state
  const [pooledSS, setPooledSS] = useState({ bills: '', coins_quarters: '' });
  const [pooledVac, setPooledVac] = useState({ coins_quarters: '' });
  const [pooledVending, setPooledVending] = useState({ bills: '', coins_quarters: '' });
  const [pooledDog, setPooledDog] = useState({ bills: '', coins_quarters: '' });
  const [pooledAuto, setPooledAuto] = useState({ coins_in_dollars: '', coins_out_quarters: '', bills_out_dollars: '' });
  const [pooledChanger, setPooledChanger] = useState({ coins_in_dollars: '', coins_out_quarters: '', bills_out_dollars: '' });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update pooled defaults when location changes (new cash outs only)
  useEffect(() => {
    if (!isEditMode && currentLocation) {
      setSsPooled(currentLocation.default_ss_pooled || false);
      setVacPooled(currentLocation.default_vac_pooled || false);
      setVendingPooled(currentLocation.default_vending_pooled || false);
      setDogPooled(currentLocation.default_dog_pooled || false);
      setAutoPooled(currentLocation.default_auto_pooled || false);
      setChangerPooled(currentLocation.default_changer_pooled || false);
    }
  }, [currentLocation?.id]);

  // Resize form sections when location settings change (new cash outs only)
  useEffect(() => {
    if (!isEditMode) {
      setFormData(prev => ({
        ...prev,
        changers: makeChangers(numChangers),
        auto_washes: makeAutoWashes(numAutoWashes),
        ss_bays: makeSSBays(numSSBays),
        vacuums: makeVacuums(numVacuums),
        vending_machines: makeVending(vendingNames),
        dog_washes: makeDogWashes(numDogWashes),
      }));
    }
  }, [numChangers, numAutoWashes, numSSBays, numVacuums, numDogWashes, JSON.stringify(vendingNames)]);

  useEffect(() => {
    if (isEditMode) {
      loadCashOut();
    }
  }, [id, currentLocation?.id]);

  // Pre-fill changer coins_in from last fill data (new cash outs only)
  useEffect(() => {
    if (isEditMode) return;
    getChangerStatus().then(data => {
      if (!data?.changers?.length) return;
      setFormData(prev => ({
        ...prev,
        changers: prev.changers.map(c => {
          const fill = data.changers.find(f => f.changer_number === c.changer_number);
          return fill && fill.buckets > 0
            ? { ...c, coins_in_dollars: fill.buckets }
            : c;
        })
      }));
    }).catch(() => {});
  }, [currentLocation?.id]);

  const toNum = (v) => parseFloat(v) || 0;

  // Toggle handlers: sum individual -> pooled, distribute pooled -> individual
  const handleSSPooledToggle = (newPooled) => {
    if (newPooled && !ssPooled) {
      // Individual -> Pooled: sum all individual values
      const totalBills = formData.ss_bays.reduce((sum, b) => sum + toNum(b.bills), 0);
      const totalQuarters = formData.ss_bays.reduce((sum, b) => sum + toNum(b.coins_quarters), 0);
      setPooledSS({ bills: totalBills || '', coins_quarters: totalQuarters || '' });
    } else if (!newPooled && ssPooled) {
      // Pooled -> Individual: distribute evenly
      const count = numSSBays;
      const perBayBills = count > 0 ? Math.round(toNum(pooledSS.bills) / count * 100) / 100 : 0;
      const perBayQuarters = count > 0 ? Math.round(toNum(pooledSS.coins_quarters) / count) : 0;
      setFormData(prev => ({
        ...prev,
        ss_bays: makeSSBays(count).map((b, i) => ({
          ...b,
          bills: perBayBills || '',
          coins_quarters: perBayQuarters || ''
        }))
      }));
    }
    setSsPooled(newPooled);
  };

  const handleVacPooledToggle = (newPooled) => {
    if (newPooled && !vacPooled) {
      const totalQuarters = formData.vacuums.reduce((sum, v) => sum + toNum(v.coins_quarters), 0);
      setPooledVac({ coins_quarters: totalQuarters || '' });
    } else if (!newPooled && vacPooled) {
      const count = numVacuums;
      const perVacQuarters = count > 0 ? Math.round(toNum(pooledVac.coins_quarters) / count) : 0;
      setFormData(prev => ({
        ...prev,
        vacuums: makeVacuums(count).map(v => ({
          ...v,
          coins_quarters: perVacQuarters || ''
        }))
      }));
    }
    setVacPooled(newPooled);
  };

  const handleDogPooledToggle = (newPooled) => {
    if (newPooled && !dogPooled) {
      const totalBills = formData.dog_washes.reduce((sum, dw) => sum + toNum(dw.bills), 0);
      const totalQuarters = formData.dog_washes.reduce((sum, dw) => sum + toNum(dw.coins_quarters), 0);
      setPooledDog({ bills: totalBills || '', coins_quarters: totalQuarters || '' });
    } else if (!newPooled && dogPooled) {
      const count = numDogWashes;
      const perBills = count > 0 ? Math.round(toNum(pooledDog.bills) / count * 100) / 100 : 0;
      const perQuarters = count > 0 ? Math.round(toNum(pooledDog.coins_quarters) / count) : 0;
      setFormData(prev => ({
        ...prev,
        dog_washes: makeDogWashes(count).map(dw => ({
          ...dw,
          bills: perBills || '',
          coins_quarters: perQuarters || ''
        }))
      }));
    }
    setDogPooled(newPooled);
  };

  const handleVendingPooledToggle = (newPooled) => {
    if (newPooled && !vendingPooled) {
      const totalBills = formData.vending_machines.reduce((sum, vm) => sum + toNum(vm.bills), 0);
      const totalQuarters = formData.vending_machines.reduce((sum, vm) => sum + toNum(vm.coins_quarters), 0);
      setPooledVending({ bills: totalBills || '', coins_quarters: totalQuarters || '' });
    } else if (!newPooled && vendingPooled) {
      const count = vendingNames.length;
      const perVmBills = count > 0 ? Math.round(toNum(pooledVending.bills) / count * 100) / 100 : 0;
      const perVmQuarters = count > 0 ? Math.round(toNum(pooledVending.coins_quarters) / count) : 0;
      setFormData(prev => ({
        ...prev,
        vending_machines: makeVending(vendingNames).map(vm => ({
          ...vm,
          bills: perVmBills || '',
          coins_quarters: perVmQuarters || ''
        }))
      }));
    }
    setVendingPooled(newPooled);
  };

  const loadCashOut = async () => {
    try {
      setLoading(true);
      const data = await getCashOut(id);
      // Convert stored dollar values back to buckets for display
      const dollarsToBuckets = (dollars) => parseFloat(dollars) / 250 || 0;
      const changers = data.changers.map(c => ({
        ...c,
        coins_in_dollars: dollarsToBuckets(c.coins_in_dollars)
      }));
      // Support both new auto_washes array and legacy auto_wash single object
      let auto_washes;
      if (data.auto_washes && data.auto_washes.length > 0) {
        auto_washes = data.auto_washes.map(aw => ({
          ...aw,
          coins_in_dollars: dollarsToBuckets(aw.coins_in_dollars)
        }));
      } else if (data.auto_wash) {
        auto_washes = [{
          ...data.auto_wash,
          wash_number: 1,
          coins_in_dollars: dollarsToBuckets(data.auto_wash.coins_in_dollars)
        }];
      } else {
        auto_washes = makeAutoWashes(numAutoWashes);
      }

      // Load changer mode
      setChangerMode(data.changer_mode || 'detailed');

      // Load inclusion flags
      setSsIncluded(data.ss_included !== false);
      setAutoIncluded(data.auto_included !== false);
      setVacIncluded(data.vac_included !== false);
      setVendingIncluded(data.vending_included !== false);
      setDogIncluded(data.dog_included !== false);

      // Load pooled flags
      const loadedSsPooled = data.ss_pooled || false;
      const loadedVacPooled = data.vac_pooled || false;
      const loadedVendingPooled = data.vending_pooled || false;
      const loadedDogPooled = data.dog_pooled || false;
      const loadedAutoPooled = data.auto_pooled || false;
      const loadedChangerPooled = data.changer_pooled || false;
      setSsPooled(loadedSsPooled);
      setVacPooled(loadedVacPooled);
      setVendingPooled(loadedVendingPooled);
      setDogPooled(loadedDogPooled);
      setAutoPooled(loadedAutoPooled);
      setChangerPooled(loadedChangerPooled);

      // If a section was saved pooled, seed the aggregate inputs with the
      // sum across the per-unit records so re-editing shows the same total.
      if (loadedAutoPooled && (data.auto_washes || []).length > 0) {
        const sum = data.auto_washes.reduce((acc, aw) => ({
          coins_in_dollars: acc.coins_in_dollars + (aw.coins_in_dollars || 0),
          coins_out_quarters: acc.coins_out_quarters + (aw.coins_out_quarters || 0),
          bills_out_dollars: acc.bills_out_dollars + (aw.bills_out_dollars || 0),
        }), { coins_in_dollars: 0, coins_out_quarters: 0, bills_out_dollars: 0 });
        setPooledAuto({
          coins_in_dollars: coinsInUnit === 'buckets'
            ? dollarsToBuckets(sum.coins_in_dollars)
            : sum.coins_in_dollars,
          coins_out_quarters: sum.coins_out_quarters,
          bills_out_dollars: sum.bills_out_dollars,
        });
      }
      if (loadedChangerPooled && (data.changers || []).length > 0) {
        const sum = data.changers.reduce((acc, c) => ({
          coins_in_dollars: acc.coins_in_dollars + (c.coins_in_dollars || 0),
          coins_out_quarters: acc.coins_out_quarters + (c.coins_out_quarters || 0),
          bills_out_dollars: acc.bills_out_dollars + (c.bills_out_dollars || 0),
        }), { coins_in_dollars: 0, coins_out_quarters: 0, bills_out_dollars: 0 });
        setPooledChanger({
          coins_in_dollars: coinsInUnit === 'buckets'
            ? dollarsToBuckets(sum.coins_in_dollars)
            : sum.coins_in_dollars,
          coins_out_quarters: sum.coins_out_quarters,
          bills_out_dollars: sum.bills_out_dollars,
        });
      }

      // If pooled, load pooled values from the single record
      if (loadedSsPooled && data.ss_bays.length > 0) {
        const rec = data.ss_bays[0];
        setPooledSS({ bills: rec.bills, coins_quarters: rec.coins_quarters });
      }
      if (loadedVacPooled && data.vacuums.length > 0) {
        const rec = data.vacuums[0];
        setPooledVac({ coins_quarters: rec.coins_quarters });
      }
      if (loadedVendingPooled && data.vending_machines.length > 0) {
        const rec = data.vending_machines[0];
        setPooledVending({ bills: rec.bills, coins_quarters: rec.coins_quarters });
      }
      if (loadedDogPooled && (data.dog_washes || []).length > 0) {
        const rec = data.dog_washes[0];
        setPooledDog({ bills: rec.bills, coins_quarters: rec.coins_quarters });
      }

      setFormData({
        date: data.date,
        changers,
        auto_washes,
        ss_bays: loadedSsPooled ? makeSSBays(numSSBays) : data.ss_bays,
        vacuums: loadedVacPooled ? makeVacuums(numVacuums) : data.vacuums,
        vending_machines: loadedVendingPooled ? makeVending(vendingNames) : data.vending_machines,
        dog_washes: loadedDogPooled ? makeDogWashes(numDogWashes) : (data.dog_washes || []),
        quarter_buckets_deposited: data.quarter_buckets_deposited ?? '',
      });
    } catch (error) {
      setError('Failed to load cash out data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Convert buckets to dollars (1 bucket = 1000 quarters = $250)
  const bucketsToDoller = (buckets) => {
    return parseFloat(buckets) * 250 || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Helper function to convert empty strings to 0
      const toNumber = (value) => {
        if (value === '' || value === null || value === undefined) return 0;
        return parseFloat(value) || 0;
      };

      // Convert coins_in from buckets to dollars if needed and fill blanks with 0
      const submitData = { ...formData };

      // Include pooled flags
      submitData.ss_pooled = ssPooled;
      submitData.vac_pooled = vacPooled;
      submitData.vending_pooled = vendingPooled;
      submitData.dog_pooled = dogPooled;
      submitData.auto_pooled = autoPooled && numAutoWashes > 1;
      submitData.changer_pooled = changerPooled;
      submitData.quarter_buckets_deposited = Number(formData.quarter_buckets_deposited) || 0;

      // Include section inclusion flags
      submitData.ss_included = ssIncluded;
      submitData.auto_included = autoIncluded;
      submitData.vac_included = vacIncluded;
      submitData.vending_included = vendingIncluded;
      submitData.dog_included = dogIncluded;

      // Include changer mode
      submitData.changer_mode = changerMode;

      // Clear data for excluded sections
      if (!ssIncluded) submitData.ss_bays = [];
      if (!autoIncluded) submitData.auto_washes = [];
      if (!vacIncluded) submitData.vacuums = [];
      if (!vendingIncluded) submitData.vending_machines = [];
      if (!dogIncluded || numDogWashes === 0) submitData.dog_washes = [];

      // Process changers — when pooled, one aggregate row (number 0)
      // distributes evenly across the configured changers so per-unit
      // rollups downstream (overage/shortfall) still add up correctly.
      if (changerPooled) {
        const perCount = Math.max(1, numChangers);
        const billsOut = toNumber(pooledChanger.bills_out_dollars);
        if (changerMode === 'simple') {
          const each = billsOut / perCount;
          submitData.changers = Array.from({ length: perCount }, (_, i) => ({
            changer_number: i + 1,
            coins_in_dollars: each,
            coins_out_quarters: 0,
            bills_out_dollars: each,
          }));
        } else {
          const coinsIn = coinsInUnit === 'buckets'
            ? bucketsToDoller(pooledChanger.coins_in_dollars)
            : toNumber(pooledChanger.coins_in_dollars);
          const coinsOut = toNumber(pooledChanger.coins_out_quarters);
          submitData.changers = Array.from({ length: perCount }, (_, i) => ({
            changer_number: i + 1,
            coins_in_dollars: coinsIn / perCount,
            coins_out_quarters: Math.round(coinsOut / perCount),
            bills_out_dollars: billsOut / perCount,
          }));
        }
      } else {
        submitData.changers = submitData.changers.map(c => {
          const billsOut = toNumber(c.bills_out_dollars);
          if (changerMode === 'simple') {
            return {
              changer_number: c.changer_number,
              coins_in_dollars: billsOut,
              coins_out_quarters: 0,
              bills_out_dollars: billsOut
            };
          }
          return {
            changer_number: c.changer_number,
            coins_in_dollars: coinsInUnit === 'buckets' ? bucketsToDoller(c.coins_in_dollars) : toNumber(c.coins_in_dollars),
            coins_out_quarters: toNumber(c.coins_out_quarters),
            bills_out_dollars: billsOut
          };
        });
      }

      // Process auto washes — same pooling pattern as changers.
      if (autoPooled && numAutoWashes > 1) {
        const perCount = numAutoWashes;
        const billsOut = toNumber(pooledAuto.bills_out_dollars);
        if (changerMode === 'simple') {
          const each = billsOut / perCount;
          submitData.auto_washes = Array.from({ length: perCount }, (_, i) => ({
            wash_number: i + 1,
            coins_in_dollars: 0,
            coins_out_quarters: 0,
            bills_out_dollars: each,
          }));
        } else {
          const coinsIn = coinsInUnit === 'buckets'
            ? bucketsToDoller(pooledAuto.coins_in_dollars)
            : toNumber(pooledAuto.coins_in_dollars);
          const coinsOut = toNumber(pooledAuto.coins_out_quarters);
          submitData.auto_washes = Array.from({ length: perCount }, (_, i) => ({
            wash_number: i + 1,
            coins_in_dollars: coinsIn / perCount,
            coins_out_quarters: Math.round(coinsOut / perCount),
            bills_out_dollars: billsOut / perCount,
          }));
        }
      } else {
        submitData.auto_washes = submitData.auto_washes.map(aw => {
          const billsOut = toNumber(aw.bills_out_dollars);
          if (changerMode === 'simple') {
            return {
              wash_number: aw.wash_number,
              coins_in_dollars: 0,
              coins_out_quarters: 0,
              bills_out_dollars: billsOut
            };
          }
          return {
            wash_number: aw.wash_number,
            coins_in_dollars: coinsInUnit === 'buckets' ? bucketsToDoller(aw.coins_in_dollars) : toNumber(aw.coins_in_dollars),
            coins_out_quarters: toNumber(aw.coins_out_quarters),
            bills_out_dollars: billsOut
          };
        });
      }

      // Process SS bays
      if (ssPooled) {
        submitData.ss_bays = [{
          bay_number: 0,
          bills: toNumber(pooledSS.bills),
          coins_quarters: toNumber(pooledSS.coins_quarters)
        }];
      } else {
        submitData.ss_bays = submitData.ss_bays.map(b => ({
          bay_number: b.bay_number,
          bills: toNumber(b.bills),
          coins_quarters: toNumber(b.coins_quarters)
        }));
      }

      // Process vacuums
      if (vacPooled) {
        submitData.vacuums = [{
          vacuum_number: 0,
          coins_quarters: toNumber(pooledVac.coins_quarters)
        }];
      } else {
        submitData.vacuums = submitData.vacuums.map(v => ({
          vacuum_number: v.vacuum_number,
          coins_quarters: toNumber(v.coins_quarters)
        }));
      }

      // Process vending machines
      if (vendingPooled) {
        submitData.vending_machines = [{
          machine_name: 'All Vending',
          bills: toNumber(pooledVending.bills),
          coins_quarters: toNumber(pooledVending.coins_quarters)
        }];
      } else {
        submitData.vending_machines = submitData.vending_machines.map(vm => ({
          machine_name: vm.machine_name,
          bills: toNumber(vm.bills),
          coins_quarters: toNumber(vm.coins_quarters)
        }));
      }

      // Process dog washes
      if (numDogWashes > 0 && dogIncluded) {
        if (dogPooled) {
          submitData.dog_washes = [{
            dog_wash_number: 0,
            bills: toNumber(pooledDog.bills),
            coins_quarters: toNumber(pooledDog.coins_quarters)
          }];
        } else {
          submitData.dog_washes = (submitData.dog_washes || []).map(dw => ({
            dog_wash_number: dw.dog_wash_number,
            bills: toNumber(dw.bills),
            coins_quarters: toNumber(dw.coins_quarters)
          }));
        }
      }

      if (isEditMode) {
        await updateCashOut(id, submitData);
        navigate('/');
      } else {
        const created = await createCashOut(submitData);
        track('cashout_save');
        navigate(`/cash-out/${created.id}`);
      }
    } catch (error) {
      setError('Failed to save cash out. Please try again.');
      console.error(error);
      setLoading(false);
    }
  };

  const updateChanger = (index, field, value) => {
    const newChangers = [...formData.changers];
    newChangers[index][field] = value === '' ? '' : value;
    setFormData({ ...formData, changers: newChangers });
  };

  const updateAutoWash = (index, field, value) => {
    const newAutoWashes = [...formData.auto_washes];
    newAutoWashes[index] = { ...newAutoWashes[index], [field]: value === '' ? '' : value };
    setFormData({ ...formData, auto_washes: newAutoWashes });
  };

  const updateSSBay = (index, field, value) => {
    const newBays = [...formData.ss_bays];
    newBays[index][field] = value === '' ? '' : value;
    setFormData({ ...formData, ss_bays: newBays });
  };

  const updateVacuum = (index, field, value) => {
    const newVacuums = [...formData.vacuums];
    newVacuums[index][field] = value === '' ? '' : value;
    setFormData({ ...formData, vacuums: newVacuums });
  };

  const updateVendingMachine = (index, field, value) => {
    const newVending = [...formData.vending_machines];
    newVending[index][field] = value === '' ? '' : value;
    setFormData({ ...formData, vending_machines: newVending });
  };

  const updateDogWash = (index, field, value) => {
    const newDogs = [...formData.dog_washes];
    newDogs[index][field] = value === '' ? '' : value;
    setFormData({ ...formData, dog_washes: newDogs });
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Cash Out' : 'New Cash Out'}
        </h1>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date and Units */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                style={{ WebkitAppearance: 'none', maxWidth: '100%', boxSizing: 'border-box', minHeight: '42px' }}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-base sm:text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coins In Units
              </label>
              <select
                value={coinsInUnit}
                onChange={(e) => setCoinsInUnit(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 w-full"
              >
                <option value="buckets">Buckets (1 bucket = 1000 quarters)</option>
                <option value="dollars">Dollars</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section Selection */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Sections to Include</h2>
          <p className="text-xs text-gray-500 mb-3">Uncheck any sections you're not counting in this cash out. Their revenue will carry over to your next cash out.</p>
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'SS Bays', value: ssIncluded, setter: setSsIncluded },
              { label: 'Auto Wash', value: autoIncluded, setter: setAutoIncluded },
              { label: 'Vacuums', value: vacIncluded, setter: setVacIncluded },
              { label: 'Vending', value: vendingIncluded, setter: setVendingIncluded },
              ...(numDogWashes > 0
                ? [{ label: 'Dog Wash', value: dogIncluded, setter: setDogIncluded }]
                : []),
            ].map(({ label, value, setter }) => (
              <label key={label} className={`inline-flex items-center px-3 py-2 rounded-lg border cursor-pointer select-none text-sm font-medium transition-colors ${
                value
                  ? 'bg-blue-50 border-blue-300 text-blue-800'
                  : 'bg-gray-50 border-gray-200 text-gray-400'
              }`}>
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => setter(e.target.checked)}
                  className="sr-only"
                />
                <span className={`w-4 h-4 mr-2 rounded border flex items-center justify-center ${
                  value ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'
                }`}>
                  {value && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </span>
                {label}
              </label>
            ))}
          </div>
        </div>

        {/* Bill Changers */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-xl font-semibold text-gray-900">Bill Changers</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex rounded-md shadow-sm" role="group">
                <button
                  type="button"
                  onClick={() => setChangerMode('simple')}
                  className={`px-3 py-1 text-xs font-medium rounded-l-md border ${
                    changerMode === 'simple'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Bills Only
                </button>
                <button
                  type="button"
                  onClick={() => setChangerMode('detailed')}
                  className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-b border-r ${
                    changerMode === 'detailed'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Detailed
                </button>
              </div>
              <PoolToggle pooled={changerPooled} onToggle={setChangerPooled} />
            </div>
          </div>
          {changerMode === 'simple' && (
            <p className="text-xs text-gray-500 mb-4">Bills Only mode assumes changers are balanced — no overage/shortfall will be calculated.</p>
          )}
          {changerPooled ? (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Combined ({numChangers} changer{numChangers === 1 ? '' : 's'})</h3>
              <div className="space-y-3">
                {changerMode === 'detailed' && (
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Coins In total ({coinsInUnit === 'buckets' ? 'buckets' : '$'})
                    </label>
                    <MathInput
                      type="text"
                      inputMode="decimal"
                      value={pooledChanger.coins_in_dollars}
                      onChange={(e) => setPooledChanger({ ...pooledChanger, coins_in_dollars: e.target.value })}
                      className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                      placeholder="0"
                    />
                  </div>
                )}
                {changerMode === 'detailed' && (
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Coins Out total (quarters)</label>
                    <MathInput
                      type="text"
                      inputMode="numeric"
                      value={pooledChanger.coins_out_quarters}
                      onChange={(e) => setPooledChanger({ ...pooledChanger, coins_out_quarters: e.target.value })}
                      className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                      placeholder="0"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Bills Out total ($)</label>
                  <MathInput
                    type="text"
                    inputMode="decimal"
                    value={pooledChanger.bills_out_dollars}
                    onChange={(e) => setPooledChanger({ ...pooledChanger, bills_out_dollars: e.target.value })}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                    placeholder="0"
                  />
                </div>
                <p className="text-xs text-gray-400">Totals are split evenly across the individual changers when saved.</p>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formData.changers.map((changer, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Changer #{changer.changer_number}</h3>
                <div className="space-y-3">
                  {changerMode === 'detailed' && (
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Coins In ({coinsInUnit === 'buckets' ? 'buckets' : '$'})
                      </label>
                      <MathInput
                        type="text"
                        inputMode="decimal"
                        value={changer.coins_in_dollars}
                        onChange={(e) => updateChanger(index, 'coins_in_dollars', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                  )}
                  {changerMode === 'detailed' && (
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Coins Out (quarters)</label>
                      <MathInput
                        type="text"
                        inputMode="numeric"
                        value={changer.coins_out_quarters}
                        onChange={(e) => updateChanger(index, 'coins_out_quarters', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Bills Out ($)</label>
                    <MathInput
                      type="text"
                      inputMode="decimal"
                      value={changer.bills_out_dollars}
                      onChange={(e) => updateChanger(index, 'bills_out_dollars', e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>

        {/* Auto Wash(es) */}
        {autoIncluded && formData.auto_washes.length > 0 && (
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h2 className="text-xl font-semibold text-gray-900">
                {formData.auto_washes.length === 1 ? 'Automatic Wash' : 'Automatic Washes'}
              </h2>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex rounded-md shadow-sm" role="group">
                  <button
                    type="button"
                    onClick={() => setChangerMode('simple')}
                    className={`px-3 py-1 text-xs font-medium rounded-l-md border ${
                      changerMode === 'simple'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Bills Only
                  </button>
                  <button
                    type="button"
                    onClick={() => setChangerMode('detailed')}
                    className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-b border-r ${
                      changerMode === 'detailed'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Detailed
                  </button>
                </div>
                {numAutoWashes > 1 && (
                  <PoolToggle pooled={autoPooled} onToggle={setAutoPooled} />
                )}
              </div>
            </div>
            {changerMode === 'simple' && (
              <p className="text-xs text-gray-500 mb-4">Bills Only mode assumes coin acceptors are balanced — no overage/shortfall will be calculated.</p>
            )}
            {autoPooled && numAutoWashes > 1 ? (
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-3">Combined ({numAutoWashes} auto washes)</h3>
                <div className={`grid grid-cols-1 ${changerMode === 'detailed' ? 'md:grid-cols-3' : ''} gap-4`}>
                  {changerMode === 'detailed' && (
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Coins In total ({coinsInUnit === 'buckets' ? 'buckets' : '$'})
                      </label>
                      <MathInput
                        type="text"
                        inputMode="decimal"
                        value={pooledAuto.coins_in_dollars}
                        onChange={(e) => setPooledAuto({ ...pooledAuto, coins_in_dollars: e.target.value })}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                  )}
                  {changerMode === 'detailed' && (
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Coins Out total (quarters)</label>
                      <MathInput
                        type="text"
                        inputMode="numeric"
                        value={pooledAuto.coins_out_quarters}
                        onChange={(e) => setPooledAuto({ ...pooledAuto, coins_out_quarters: e.target.value })}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Bills Out total ($)</label>
                    <MathInput
                      type="text"
                      inputMode="decimal"
                      value={pooledAuto.bills_out_dollars}
                      onChange={(e) => setPooledAuto({ ...pooledAuto, bills_out_dollars: e.target.value })}
                      className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                      placeholder="0"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">Totals split evenly across the auto washes when saved.</p>
              </div>
            ) : (
            <div className={formData.auto_washes.length > 1 ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : ''}>
              {formData.auto_washes.map((aw, index) => (
                <div key={aw.wash_number} className={formData.auto_washes.length > 1 ? 'border border-gray-200 rounded-lg p-4' : ''}>
                  {formData.auto_washes.length > 1 && (
                    <h3 className="font-medium text-gray-900 mb-3">Auto Wash #{aw.wash_number}</h3>
                  )}
                  <div className={`grid grid-cols-1 ${changerMode === 'detailed' ? 'md:grid-cols-3' : ''} gap-4`}>
                    {changerMode === 'detailed' && (
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Coins In ({coinsInUnit === 'buckets' ? 'buckets' : '$'})
                        </label>
                        <MathInput
                          type="text"
                          inputMode="decimal"
                          value={aw.coins_in_dollars}
                          onChange={(e) => updateAutoWash(index, 'coins_in_dollars', e.target.value)}
                          className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                          placeholder="0"
                        />
                      </div>
                    )}
                    {changerMode === 'detailed' && (
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Coins Out (quarters)</label>
                        <MathInput
                          type="text"
                          inputMode="numeric"
                          value={aw.coins_out_quarters}
                          onChange={(e) => updateAutoWash(index, 'coins_out_quarters', e.target.value)}
                          className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                          placeholder="0"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Bills Out ($)</label>
                      <MathInput
                        type="text"
                        inputMode="decimal"
                        value={aw.bills_out_dollars}
                        onChange={(e) => updateAutoWash(index, 'bills_out_dollars', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            )}
          </div>
        )}

        {/* Self-Service Bays */}
        {ssIncluded && <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Self-Service Bays</h2>
            <PoolToggle pooled={ssPooled} onToggle={handleSSPooledToggle} />
          </div>
          {ssPooled ? (
            <div className="border border-gray-200 rounded-lg p-4 max-w-md">
              <h3 className="font-medium text-gray-900 mb-3">All SS Bays</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Bills ($)</label>
                  <MathInput
                    type="text"
                    inputMode="decimal"
                    value={pooledSS.bills}
                    onChange={(e) => setPooledSS(prev => ({ ...prev, bills: e.target.value === '' ? '' : e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Coins (quarters)</label>
                  <MathInput
                    type="text"
                    inputMode="numeric"
                    value={pooledSS.coins_quarters}
                    onChange={(e) => setPooledSS(prev => ({ ...prev, coins_quarters: e.target.value === '' ? '' : e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.ss_bays.map((bay, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Bay #{bay.bay_number}</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Bills ($)</label>
                      <MathInput
                        type="text"
                        inputMode="decimal"
                        value={bay.bills}
                        onChange={(e) => updateSSBay(index, 'bills', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Coins (quarters)</label>
                      <MathInput
                        type="text"
                        inputMode="numeric"
                        value={bay.coins_quarters}
                        onChange={(e) => updateSSBay(index, 'coins_quarters', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>}

        {/* Vacuums */}
        {vacIncluded && <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Vacuums</h2>
            <PoolToggle pooled={vacPooled} onToggle={handleVacPooledToggle} />
          </div>
          {vacPooled ? (
            <div className="border border-gray-200 rounded-lg p-4 max-w-md">
              <h3 className="font-medium text-gray-900 mb-3">All Vacuums</h3>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Coins (quarters)</label>
                <MathInput
                  type="text"
                  inputMode="numeric"
                  value={pooledVac.coins_quarters}
                  onChange={(e) => setPooledVac({ coins_quarters: e.target.value === '' ? '' : e.target.value })}
                  className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                  placeholder="0"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {formData.vacuums.map((vac, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vac #{vac.vacuum_number}
                  </label>
                  <MathInput
                    type="text"
                    inputMode="numeric"
                    value={vac.coins_quarters}
                    onChange={(e) => updateVacuum(index, 'coins_quarters', e.target.value)}
                    className="border border-gray-300 rounded-md px-2 py-1 w-full text-sm no-spinner"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          )}
        </div>}

        {/* Vending Machines */}
        {vendingIncluded && <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Vending Machines</h2>
            <PoolToggle pooled={vendingPooled} onToggle={handleVendingPooledToggle} />
          </div>
          {vendingPooled ? (
            <div className="border border-gray-200 rounded-lg p-4 max-w-md">
              <h3 className="font-medium text-gray-900 mb-3">All Vending</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Bills ($)</label>
                  <MathInput
                    type="text"
                    inputMode="decimal"
                    value={pooledVending.bills}
                    onChange={(e) => setPooledVending(prev => ({ ...prev, bills: e.target.value === '' ? '' : e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Coins (quarters)</label>
                  <MathInput
                    type="text"
                    inputMode="numeric"
                    value={pooledVending.coins_quarters}
                    onChange={(e) => setPooledVending(prev => ({ ...prev, coins_quarters: e.target.value === '' ? '' : e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {formData.vending_machines.map((vm, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">{vm.machine_name}</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Bills ($)</label>
                      <MathInput
                        type="text"
                        inputMode="decimal"
                        value={vm.bills}
                        onChange={(e) => updateVendingMachine(index, 'bills', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Coins (quarters)</label>
                      <MathInput
                        type="text"
                        inputMode="numeric"
                        value={vm.coins_quarters}
                        onChange={(e) => updateVendingMachine(index, 'coins_quarters', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>}

        {/* Dog Wash — only rendered when the location has one configured */}
        {numDogWashes > 0 && dogIncluded && <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <div className="flex items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {numDogWashes === 1 ? 'Dog Wash' : 'Dog Washes'}
            </h2>
            {numDogWashes > 1 && (
              <PoolToggle pooled={dogPooled} onToggle={handleDogPooledToggle} />
            )}
          </div>
          {dogPooled && numDogWashes > 1 ? (
            <div className="border border-gray-200 rounded-lg p-4 max-w-md">
              <h3 className="font-medium text-gray-900 mb-3">All Dog Washes</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Bills ($)</label>
                  <MathInput
                    type="text"
                    inputMode="decimal"
                    value={pooledDog.bills}
                    onChange={(e) => setPooledDog(prev => ({ ...prev, bills: e.target.value === '' ? '' : e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Coins (quarters)</label>
                  <MathInput
                    type="text"
                    inputMode="numeric"
                    value={pooledDog.coins_quarters}
                    onChange={(e) => setPooledDog(prev => ({ ...prev, coins_quarters: e.target.value === '' ? '' : e.target.value }))}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(formData.dog_washes || []).map((dw, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">
                    {numDogWashes === 1 ? 'Dog Wash' : `Dog Wash #${dw.dog_wash_number}`}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Bills ($)</label>
                      <MathInput
                        type="text"
                        inputMode="decimal"
                        value={dw.bills}
                        onChange={(e) => updateDogWash(index, 'bills', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">Coins (quarters)</label>
                      <MathInput
                        type="text"
                        inputMode="numeric"
                        value={dw.coins_quarters}
                        onChange={(e) => updateDogWash(index, 'coins_quarters', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 w-full no-spinner"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>}

        {/* Bank Deposit — quarters going to the bank in $250 buckets.
            Recorded here so a running tally can be reconciled with the
            bank statement without guesswork. */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Bank Deposit</h2>
          <p className="text-sm text-gray-500 mb-4">
            Track how many buckets of quarters (1,000 quarters = $250 each) you're depositing at the bank.
          </p>
          <div className="flex items-center gap-3">
            <MathInput
              type="text"
              inputMode="numeric"
              value={formData.quarter_buckets_deposited ?? ''}
              onChange={(e) => setFormData({ ...formData, quarter_buckets_deposited: e.target.value })}
              className="border border-gray-300 rounded-md px-3 py-2 w-24 no-spinner"
              placeholder="0"
            />
            <span className="text-sm text-gray-700">
              bucket{Number(formData.quarter_buckets_deposited || 0) === 1 ? '' : 's'}
              {Number(formData.quarter_buckets_deposited || 0) > 0 && (
                <span className="text-gray-400 ml-2">
                  (${(Number(formData.quarter_buckets_deposited || 0) * 250).toLocaleString('en-US')} in quarters)
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Cash Out' : 'Create Cash Out'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CashOutForm;
